/**
 * ContrastMeter — the two Hero meters (Strength among knowers / Visibility
 * in market). Bipolar -100..+100 score on the same red→teal gradient + dark
 * position-pin gauge as every other bar in the marketer view (matches
 * `BrandEquityVisualizationV3.tsx:1132-1174`).
 *
 * The number itself renders in the supplied tone (green for strength, red
 * for visibility-as-gap) per the contrast framing — the gap between the two
 * numbers IS the marketer message.
 */

import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import { formatBipolarScore } from '../config/bandColours';
import { THEME } from '../config/theme';
import { Skeleton } from '../atoms';

const GRADIENT_NORMAL =
  'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)';

export interface ContrastMeterProps {
  /** Raw label (e.g. an AI-picked element) — takes precedence over labelId. */
  label?: React.ReactNode;
  /** Localised label (FormattedMessage) — used when `label` is absent. */
  labelId?: string;
  defaultLabel?: string;
  /** Bipolar score on -100..+100 (same scale as every other meter). */
  value: number;
  /** Accent tone for the number — green for strength, red for visibility-as-gap. */
  tone: string;
  caption?: string;
  /** Skeleton the caption while the per-element AI summary is still loading. */
  loading?: boolean;
  /** When set, the title + bar + value open the element drawer (the caption's
   *  Show more/less toggle stays independent). Omit for the non-element fallback
   *  meters (love/know group averages), which have no single element to open. */
  onOpen?: () => void;
}

/** Collapsed caption height — ~3 readable lines (13px / 1.55 ≈ 20px) + a faded
 *  sliver of the next, before "Show more". */
const CAPTION_COLLAPSED_MAX = 74;
/** Only offer collapse when the caption is long enough to overflow ~3 lines. */
const CAPTION_COLLAPSE_THRESHOLD = 150;

export const ContrastMeter: React.FunctionComponent<ContrastMeterProps> = ({
  label,
  labelId,
  defaultLabel,
  value,
  tone,
  caption,
  loading,
  onOpen,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hover, setHover] = useState(false);
  const interactive = typeof onOpen === 'function';
  const captionText = typeof caption === 'string' ? caption : '';
  const collapsible = captionText.length > CAPTION_COLLAPSE_THRESHOLD;
  // Bipolar -100..+100 → 0..100 position along the gauge.
  const indicatorPosition = Math.max(0, Math.min(100, ((value + 100) / 200) * 100));
  return (
    <div>
      <div
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? () => onOpen?.() : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen?.();
                }
              }
            : undefined
        }
        onMouseEnter={interactive ? () => setHover(true) : undefined}
        onMouseLeave={interactive ? () => setHover(false) : undefined}
        style={{
          cursor: interactive ? 'pointer' : 'default',
          borderRadius: 10,
          padding: '6px 8px',
          margin: '-6px -8px',
          background: interactive && hover ? 'rgba(24,33,109,0.05)' : 'transparent',
          transition: 'background 120ms ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 16,
            marginBottom: 8,
          }}
        >
        <span
          style={{
            fontSize: 15,
            color: THEME.textBase,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            maxWidth: 240,
          }}
        >
          {label ?? (
            <FormattedMessage id={labelId ?? ' '} defaultMessage={defaultLabel ?? ''} />
          )}
        </span>
        <span
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: tone,
            letterSpacing: '-0.02em',
          }}
        >
          {formatBipolarScore(value)}
        </span>
      </div>
      {/* v3-matched gauge: gradient + zero marker + pin */}
      <div
        style={{
          position: 'relative',
          height: 14,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 6,
            background: GRADIENT_NORMAL,
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: -1,
              bottom: -1,
              width: 1,
              backgroundColor: 'rgba(0,0,0,0.4)',
              transform: 'translateX(-50%)',
            }}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            left: `${indicatorPosition}%`,
            top: 0,
            width: 3,
            height: 14,
            backgroundColor: '#1f2937',
            borderRadius: 2,
            transform: 'translateX(-50%)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
        </div>
      </div>
      {loading ? (
        <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Skeleton height={12} width="100%" />
          <Skeleton height={12} width="92%" />
          <Skeleton height={12} width="64%" />
        </div>
      ) : (
        caption && (
          <div style={{ marginTop: 9 }}>
            <div
              style={{
                position: 'relative',
                fontSize: 13,
                color: THEME.textSecondary,
                lineHeight: 1.55,
                maxHeight: collapsible && !expanded ? CAPTION_COLLAPSED_MAX : undefined,
                overflow: collapsible && !expanded ? 'hidden' : undefined,
              }}
            >
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <span style={{ display: 'block', marginBottom: 4 }}>{children}</span>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: 700, color: THEME.textBase }}>
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => <em>{children}</em>,
                }}
              >
                {caption}
              </ReactMarkdown>
              {/* Fade the clamped text into the panel surface when collapsed. */}
              {collapsible && !expanded && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 14,
                    background: `linear-gradient(to bottom, rgba(250,251,252,0), ${THEME.bgTinted})`,
                  }}
                />
              )}
            </div>
            {collapsible && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                style={{
                  marginTop: 4,
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: THEME.primary,
                }}
              >
                {expanded ? (
                  <FormattedMessage id="marketerView.hero.showLess" defaultMessage="Show less" />
                ) : (
                  <FormattedMessage id="marketerView.hero.showMore" defaultMessage="Show more" />
                )}
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
};
